import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from '@mui/material';
import { alpha, ThemeProvider } from '@mui/material/styles';
import store from 'app/store';
import { resolveTheme } from 'app/utils/useCurrentTheme';
import { confirmAlert } from 'react-confirm-alert';

type DestinationSupplierOptions = {
  /**
   * The destination supplier's `vehicleDetails.fullyRefundableUntil` value
   * (e.g. "12 hours before pick up", "1 hour before pick up"). When omitted
   * we fall back to a generic message.
   */
  fullyRefundableUntil?: string
}

const handleDestinationSupplier = async (
  action: any,
  options: DestinationSupplierOptions = {},
) => {
  const { fullyRefundableUntil } = options
  // react-confirm-alert portals customUI outside the React tree so the app's
  // ThemeProvider doesn't reach it (MUI falls back to its default blue
  // primary). We resolve the active theme synchronously and re-provide it.
  const state: any = store.getState()
  const themeName = state?.clients?.client?.applicationTheme?.name
  const host = typeof window !== 'undefined' ? window.location.host : ''
  const activeTheme = resolveTheme(themeName, host)

  confirmAlert({
    customUI: ({ onClose }) => {
      return (
        <ThemeProvider theme={activeTheme}>
          <Dialog
            open={true}
            onClose={onClose}
            aria-labelledby="destination-supplier-title"
            aria-describedby="destination-supplier-body"
            maxWidth="xs"
            fullWidth
            PaperProps={{
              sx: { borderRadius: 2, overflow: 'hidden' },
            }}
          >
            <DialogTitle
              id="destination-supplier-title"
              sx={(theme) => ({
                px: 3,
                pt: 3,
                pb: 1.5,
                color: theme.palette.brand?.dark || theme.palette.text.primary,
              })}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                  sx={(theme) => ({
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.warning.main, 0.12),
                    color: theme.palette.warning.main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  })}
                >
                  <WarningAmberRoundedIcon sx={{ fontSize: 22 }} />
                </Box>
                <Typography
                  component="span"
                  sx={{
                    fontSize: '1.125rem',
                    fontWeight: 700,
                    letterSpacing: '0.01em',
                  }}
                >
                  Destination supplier
                </Typography>
              </Stack>
            </DialogTitle>
            <DialogContent
              id="destination-supplier-body"
              sx={{ px: 3, pb: 1, pt: 0 }}
            >
              <Typography
                variant="body2"
                sx={{ color: '#3b464f', lineHeight: 1.55 }}
              >
                {fullyRefundableUntil ? (
                  <>
                    Free cancellation up to{' '}
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      {fullyRefundableUntil}
                    </Box>
                    . After that you&apos;ll be charged the{' '}
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      full trip cost
                    </Box>
                    .
                  </>
                ) : (
                  <>
                    Cancel after the supplier&apos;s refund window and
                    you&apos;ll be charged the{' '}
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      full trip cost
                    </Box>
                    .
                  </>
                )}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: '#3b464f', mt: 1.5, fontWeight: 600 }}
              >
                Continue with this supplier?
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
              <Button
                onClick={onClose}
                variant="outlined"
                color="inherit"
                disableElevation
                sx={{
                  color: '#3b464f',
                  backgroundColor: '#fff',
                  borderColor: '#d0d6dc',
                  fontWeight: 600,
                  boxShadow: 'none',
                  '&:hover, &:focus': {
                    backgroundColor: '#f5f6f7',
                    borderColor: '#a8b1ba',
                    boxShadow: 'none',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  action()
                  onClose()
                }}
                sx={{ fontWeight: 700, px: 2.25 }}
              >
                Yes, continue
              </Button>
            </DialogActions>
          </Dialog>
        </ThemeProvider>
      )
    },
  })
}

export default handleDestinationSupplier
